import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import type { ID } from "../../data/BaseEntity";
import type { Product } from "../../sharedComponents/products/ProductSelect";
import ProductSelect from "../../sharedComponents/products/ProductSelect";
import { AddProductFromMapDialog } from "../products/map/AddProductFromMapDialog";
import { useProducts } from "../products/useProducts";
import type { AvailableOption } from "./ProductOptions";
import ProductOptions from "./ProductOptions";

export interface PackageProduct {
  readonly id: ID;
  readonly optional: boolean;
  readonly options: (ID | null)[];
}

interface ProductRowProps {
  readonly product: PackageProduct & { error?: boolean };
  readonly products: Product[];
  readonly availableOptions: AvailableOption[];
  readonly onChange: (value: PackageProduct) => void;
  readonly onDelete: () => void;
}

const ProductRow: FC<ProductRowProps> = ({ product, products, availableOptions, onChange, onDelete }) => {
  const { error, id, optional, options } = product;
  return (
    <TableRow data-testid="product-row">
      <TableCell>
        <ProductSelect
          value={id}
          products={products}
          error={error}
          size="small"
          fullWidth
          onChange={(selectedId) => onChange({ id: selectedId ?? "", options, optional })}
        />
      </TableCell>
      <TableCell>
        <ProductOptions
          productId={id}
          selectedOptions={options}
          availableOptions={availableOptions}
          onChange={(options) => onChange({ id, options, optional })}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={optional ?? false}
          onChange={(e) => onChange({ id, options, optional: e.target.checked })}
          aria-label="Opcjonalny produkt"
        />
      </TableCell>
      <TableCell>
        <Tooltip title="Usuń produkt">
          <IconButton aria-label="usuń produkt" color="secondary" onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

interface Props {
  products: (PackageProduct & { error?: boolean })[];
  onChange: (products: PackageProduct[]) => void;
}

const PackageProducts: FC<Props> = ({ products, onChange }) => {
  const { products: availableProducts } = useProducts();
  const [mapOpen, setMapOpen] = useState(false);
  return (
    <>
      <Table data-testid="package-products">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 250 }}>Produkt</TableCell>
            <TableCell>Domyślne opcje</TableCell>
            <TableCell>Opcjonalny</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((p, idx) => (
            <ProductRow
              key={`${p.id}-${idx}`}
              product={p}
              products={availableProducts}
              availableOptions={
                availableProducts?.find((pr) => pr.id === p.id)?.options.map((o) => ({ id: o.id, name: o.name })) ?? []
              }
              onChange={(v) => onChange(products.map((pr, productIndex) => (productIndex === idx ? v : pr)))}
              onDelete={() => onChange(products.filter((_, productIndex) => productIndex !== idx))}
            />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2}>
              <Button
                onClick={() =>
                  onChange([
                    ...products,
                    {
                      id: "",
                      optional: false,
                      options: [],
                    },
                  ])
                }
              >
                Dodaj produkt
              </Button>
              <Button onClick={() => setMapOpen(true)}>Dodaj z mapy</Button>
            </TableCell>
            <TableCell colSpan={3} />
          </TableRow>
        </TableFooter>
      </Table>
      <AddProductFromMapDialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        onAddProduct={(product) => {
          onChange([
            ...products,
            {
              id: product.id,
              optional: false,
              options: [],
            },
          ]);
          setMapOpen(false);
        }}
      />
    </>
  );
};

export default PackageProducts;
